/**
 * /invitations/accept?token=... — 초대 수락 페이지.
 *
 * 흐름:
 *   1) 토큰으로 미리보기 (공개 endpoint) — 조직 / 역할 / 초대자 표시
 *   2) 비로그인 사용자 → "가입 후 수락" / "로그인 후 수락" 버튼.
 *      이메일 prefill 위해 초대받은 이메일을 query 로 전달.
 *   3) 로그인 사용자 → "수락" 버튼. 본인 이메일 ≠ 초대 이메일이면 서버가 403.
 *      성공 시 /app/organizations 로 redirect.
 */
import { useState, type ReactNode } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  AlertTriangle,
  Building2,
  CheckCircle2,
  Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  acceptInvitation,
  previewInvitation,
  type MemberRole,
} from "@/lib/console-api";
import { extractErrorMessage } from "@/lib/api-client";
import { useAuthStore } from "@/stores/auth-store";

const ROLE_LABEL: Record<MemberRole, string> = {
  OWNER: "소유자",
  ADMIN: "관리자",
  MEMBER: "멤버",
  VIEWER: "뷰어",
};

const SHELL =
  "container flex min-h-[calc(100vh-3.5rem)] items-center justify-center py-12";

function Card({ children }: { children: ReactNode }): JSX.Element {
  return (
    <div className="w-full max-w-md rounded-lg border border-border bg-card p-8">
      {children}
    </div>
  );
}

export function InvitationAcceptPage(): JSX.Element {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const navigate = useNavigate();
  const account = useAuthStore((s) => s.account);
  const status = useAuthStore((s) => s.status);
  const [accepting, setAccepting] = useState(false);
  const [acceptError, setAcceptError] = useState<string | null>(null);
  const [accepted, setAccepted] = useState<{
    organizationId: string;
    organizationName: string;
  } | null>(null);

  const previewQuery = useQuery({
    queryKey: ["invitation-preview", token],
    queryFn: () => previewInvitation(token),
    enabled: token.length > 0,
    retry: false,
  });

  if (!token) {
    return (
      <main className={SHELL}>
        <Card>
          <div className="text-center">
            <AlertTriangle className="mx-auto h-12 w-12 text-destructive" />
            <h1 className="mt-4 text-2xl font-bold">잘못된 초대 링크</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              token 이 없습니다. 메일의 링크를 다시 확인해 주세요.
            </p>
          </div>
        </Card>
      </main>
    );
  }

  if (previewQuery.isLoading) {
    return (
      <main className={SHELL}>
        <Card>
          <div className="text-center">
            <Loader2 className="mx-auto h-10 w-10 animate-spin text-muted-foreground" />
            <p className="mt-4 text-sm text-muted-foreground">초대 정보를 확인 중…</p>
          </div>
        </Card>
      </main>
    );
  }

  if (previewQuery.isError || !previewQuery.data) {
    return (
      <main className={SHELL}>
        <Card>
          <div className="text-center">
            <AlertTriangle className="mx-auto h-12 w-12 text-destructive" />
            <h1 className="mt-4 text-2xl font-bold">초대를 찾을 수 없어요</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              {extractErrorMessage(previewQuery.error, "링크가 잘못됐거나 만료됐을 수 있어요.")}
            </p>
          </div>
        </Card>
      </main>
    );
  }

  const preview = previewQuery.data;

  if (accepted) {
    return (
      <main className={SHELL}>
        <Card>
          <div className="text-center">
            <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-500" />
            <h1 className="mt-4 text-2xl font-bold">합류 완료</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              <strong>{accepted.organizationName}</strong> 의 멤버가 됐습니다.
            </p>
            <Link
              to="/app/organizations"
              className="mt-6 inline-flex h-10 items-center justify-center rounded-md bg-primary px-5 text-sm font-medium text-primary-foreground hover:bg-primary/90"
            >
              조직 페이지로
            </Link>
          </div>
        </Card>
      </main>
    );
  }

  // 만료 / 취소 / 이미 수락된 초대.
  if (!preview.acceptable) {
    return (
      <main className={SHELL}>
        <Card>
          <div className="text-center">
            <AlertTriangle className="mx-auto h-12 w-12 text-orange-500" />
            <h1 className="mt-4 text-2xl font-bold">수락할 수 없어요</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              {preview.reason ?? "초대를 처리할 수 없습니다."}
            </p>
            <p className="mt-4 text-xs text-muted-foreground">
              초대자에게 새 초대 발송을 요청해 주세요.
            </p>
          </div>
        </Card>
      </main>
    );
  }

  const onAccept = async (): Promise<void> => {
    setAccepting(true);
    setAcceptError(null);
    try {
      const result = await acceptInvitation(token);
      setAccepted(result);
    } catch (err) {
      setAcceptError(extractErrorMessage(err, "수락에 실패했습니다."));
    } finally {
      setAccepting(false);
    }
  };

  // 비로그인 / 인증 로딩 중.
  if (status === "idle" || status === "loading") {
    return (
      <main className={SHELL}>
        <Card>
          <Header preview={preview} />
          <p className="mt-6 text-sm text-muted-foreground text-center">
            인증 상태 확인 중…
          </p>
        </Card>
      </main>
    );
  }

  if (status === "unauthenticated") {
    const emailQuery = encodeURIComponent(preview.email);
    const tokenQuery = encodeURIComponent(token);
    return (
      <main className={SHELL}>
        <Card>
          <Header preview={preview} />
          <div className="mt-6 space-y-3">
            <Link
              to={`/signup?invitation=${tokenQuery}&email=${emailQuery}`}
              className="inline-flex h-10 w-full items-center justify-center rounded-md bg-primary px-5 text-sm font-medium text-primary-foreground hover:bg-primary/90"
            >
              가입하고 합류
            </Link>
            <Link
              to={`/login?invitation=${tokenQuery}&email=${emailQuery}`}
              className="inline-flex h-10 w-full items-center justify-center rounded-md border border-input bg-background px-5 text-sm font-medium hover:bg-accent hover:text-accent-foreground"
            >
              이미 계정이 있어요
            </Link>
          </div>
          <p className="mt-4 text-xs text-muted-foreground">
            반드시 <strong>{preview.email}</strong> 로 가입 / 로그인해야 자동
            합류됩니다.
          </p>
        </Card>
      </main>
    );
  }

  // 로그인 사용자 — 본인 이메일과 초대 이메일 비교 안내.
  const emailMismatch =
    account?.email.toLowerCase() !== preview.email.toLowerCase();

  return (
    <main className={SHELL}>
      <Card>
        <Header preview={preview} />
        {emailMismatch && (
          <div className="mt-4 rounded-md border border-orange-500/30 bg-orange-500/10 px-3 py-2 text-sm">
            현재 <strong>{account?.email}</strong> 으로 로그인 중인데, 초대받은
            이메일은 <strong>{preview.email}</strong> 입니다. 다른 계정으로 다시
            로그인해 주세요.
          </div>
        )}
        {acceptError && (
          <p className="mt-4 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {acceptError}
          </p>
        )}
        <div className="mt-6 space-y-2">
          <Button
            type="button"
            className="w-full"
            disabled={accepting || emailMismatch}
            onClick={onAccept}
          >
            {accepting && <Loader2 className="h-4 w-4 animate-spin" />}
            수락하기
          </Button>
          {emailMismatch && (
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={() => {
                useAuthStore.getState().signout();
                navigate(
                  `/login?invitation=${encodeURIComponent(token)}&email=${encodeURIComponent(preview.email)}`,
                  { replace: true },
                );
              }}
            >
              {preview.email} 로 다시 로그인
            </Button>
          )}
        </div>
      </Card>
    </main>
  );
}

function Header({
  preview,
}: {
  preview: {
    organizationName: string;
    email: string;
    role: MemberRole;
    inviterName: string | null;
  };
}): JSX.Element {
  return (
    <div className="text-center">
      <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
        <Building2 className="h-6 w-6" />
      </div>
      <h1 className="mt-4 text-2xl font-bold tracking-tight">
        {preview.organizationName}
      </h1>
      <p className="mt-2 text-sm text-muted-foreground">
        {preview.inviterName?.trim() ? `${preview.inviterName}님이 ` : ""}
        <strong>{ROLE_LABEL[preview.role] ?? preview.role}</strong> 으로 초대했어요.
      </p>
    </div>
  );
}
